import { __rest } from 'tslib';
import React, { useState, useEffect } from 'react';
import { styled, Tag, SvgIcon, Button as Button$1, Input, LazyImage, Link, Col, Row, Card, BlockHeader, Pagination, Swiper, Image, Currency, OtpInput, Checkbox } from '@teko/ui';
import { Flex, Box } from 'reflexbox';
import { C as CustomLink$1 } from './CustomLink-b9e0bc02.js';
import { t } from './i18n.es.js';
import { format } from 'date-fns';
import { P as ProductGifts, a as ProductName, L as ListTile } from './MiniDropdown-86a26434.js';
import { F as FlashSaleBadge } from './FlashSaleBadge-c171d1f2.js';
import { P as ProductPrice } from './ProductPrice-aae065f6.js';
import { L as Loading, a as LogoLoading } from './Loading-432a2ca9.js';
import { T as Text$6 } from './Text-f8e12e2a.js';
import { space } from 'styled-system';
import { a as PromoItem } from './PromoItem-e9acd7fb.js';

const DisplayStatus = {
    NONE: 1,
    CONTACT: 2,
    BUYABLE: 3,
};
const StyledProductCard$1 = styled.div `
  padding: 0.3rem;
  background: #fff;
  cursor: pointer;
  display: block;
  text-decoration: none;
  color: #333;
  border-radius: 0.5rem;
  height: 100%;
`;
const StyledProductGifts = styled(ProductGifts) `
  margin-top: 0.5rem;
`;
const StyledProductName = styled(ProductName) `
  margin-top: 0.5rem;
`;
const StyledTag = styled(Tag) `
  margin-right: 0.2rem;
  margin-bottom: 0.2rem;
  font-size: 0.65rem;
`;
const ImageWrapper = styled.div `
  position: relative;
`;
const Tags = styled.div `
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
`;
const FrameWrapper = styled.div(({ frame }) => `
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  display: flex;
  flex-wrap: wrap;
  background-image: url(${frame});
  background-size: cover;
  height: 100%;
  width: 100%;
  `);
const PriceWrapper = styled.div `
  display: flex;
  margin-top: 0.5rem;
`;
const Icons = styled.div `
  display: flex;
  flex-wrap: wrap;
  margin-left: 0.2rem;
`;
const StyledIcon = styled(SvgIcon) `
  margin-left: 0.5rem;
`;
const StyledFlashSaleBadge = styled(FlashSaleBadge) `
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  border-radius: 0;
`;
const Hover = styled.div `
  padding: 0.7rem;
  border-radius: 0.5rem;
  transition: transform 0.2s;
  -webkit-font-smoothing: subpixel-antialiased;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  &:hover {
    transform: scale(1.05);
  }
`;
const CustomLink = styled.a `
  color: unset;
  text-decoration: unset;
`;
const Contact = styled.div(({ theme }) => `
  background-color: ${theme.brand.tertiary};
  color: ${theme.brand.primary};
  padding: 2px 8px;
  font-size: 0.75rem;
  border-radius: 0.25rem;
  width: fit-content;
  font-weight: 500;
  margin-top: 0.5rem;
`);
const AddToCartBtn = styled(Button$1) `
  font-weight: 500;
`;
const QuantityStepper = styled.div(({ theme }) => `
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid ${theme.brand.primary};
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
  `);
const StyledInput = styled(Input)(({ theme }) => `
    padding: 0;
    border: none;
    width: 100%;
    text-align: center;
    color: ${theme.brand.primary};
  `);
const StyledButton$3 = styled(Button$1) `
  border: none;
  height: 40px;
`;
const AddToCartWrapper = styled.div `
  margin-top: 1rem;
`;
const StyledTagStatus = styled(Tag) `
  font-size: 11px;
  border-radius: 4px;
  position: absolute;
  bottom: 0;
  left: 0;
`;
const ProductCard = (_a) => {
    var { tags = [], image, title, price, link, icons = [], coupon, gifts, flashsale, money, regionName, className, totalAvailableByStocks, displayStatus, addToCart, needManageStock, frame, contactLink, tag, showContact = false } = _a, props = __rest(_a, ["tags", "image", "title", "price", "link", "icons", "coupon", "gifts", "flashsale", "money", "regionName", "className", "totalAvailableByStocks", "displayStatus", "addToCart", "needManageStock", "frame", "contactLink", "tag", "showContact"]);
    const [qty, setQty] = useState(() => (addToCart && addToCart.quantityInCart) || 0);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (addToCart && !loading)
            setQty(addToCart.quantityInCart);
    }, [addToCart, loading]);
    const handleUpdateQty = async () => {
        if (qty && qty !== addToCart.quantityInCart) {
            setLoading(true);
            await addToCart.onChangeQuantity(qty);
            setLoading(false);
        }
        else {
            setQty(addToCart.quantityInCart);
        }
    };
    const node = (React.createElement(Hover, { className: "product-card__content" },
        React.createElement("div", null,
            React.createElement(ImageWrapper, null,
                frame && React.createElement(FrameWrapper, { frame: frame }),
                React.createElement(LazyImage, Object.assign({ imageWidth: 320, borderRadius: 8, contain: true }, image, { aspectRatio: 1 })),
                flashsale && (React.createElement(StyledFlashSaleBadge, { size: "md", endAt: flashsale.endAt, sold: flashsale.value, total: flashsale.total, stock: totalAvailableByStocks })),
                !!tags.length && !flashsale && (React.createElement(Tags, null, tags.map((tag, index) => (React.createElement(StyledTag, { key: index, outline: true }, tag))))),
                !flashsale && tag && React.createElement(StyledTagStatus, Object.assign({}, tag))),
            React.createElement("div", { className: "product-card__info" },
                React.createElement(StyledProductName, { needManageStock: needManageStock, totalAvailableByStocks: totalAvailableByStocks, name: title.name, isFlashSelling: flashsale !== null }),
                totalAvailableByStocks > 0 ||
                    displayStatus !== DisplayStatus.CONTACT ? (React.createElement(React.Fragment, null,
                    React.createElement(PriceWrapper, null,
                        React.createElement(ProductPrice, { price: price.price, listedPrice: price.listedPrice, discount: price.discount }),
                        React.createElement(Icons, null, icons.map((icon, index) => (React.createElement(StyledIcon, Object.assign({ key: index }, icon)))))),
                    gifts && (React.createElement(StyledProductGifts, { label: t('QUÀ TẶNG'), images: gifts.images })))) : (React.createElement(React.Fragment, null, displayStatus === DisplayStatus.CONTACT ? (React.createElement(Contact, null, t('LIÊN HỆ'))) : (React.createElement(Tag, { outline: true }, displayStatus)))))),
        addToCart && (React.createElement(AddToCartWrapper, null, !!addToCart.quantityInCart ? (React.createElement(QuantityStepper, null,
            React.createElement(Loading, { isLoading: loading },
                React.createElement(StyledButton$3, { onClick: (e) => {
                        e.preventDefault();
                        addToCart.onDecrease();
                    }, outline: true, block: true, variant: "primary" },
                    React.createElement(SvgIcon, { name: "subtract", themeColor: "primary", size: 22 })),
                React.createElement(StyledInput, { value: qty || '', onClick: (e) => e.preventDefault(), onChange: (e) => setQty(Number(e.target.value)), type: "number", onBlur: handleUpdateQty, onKeyDown: (e) => {
                        if (e.key === 'Enter')
                            handleUpdateQty();
                    } }),
                React.createElement(StyledButton$3, { onClick: (e) => {
                        e.preventDefault();
                        addToCart.onIncrease();
                    }, outline: true, block: true, variant: "primary" },
                    React.createElement(SvgIcon, { name: "plus", themeColor: "primary", size: 22 }))))) : (React.createElement(AddToCartBtn, { onClick: (e) => {
                e.preventDefault();
                if (showContact) {
                    if (contactLink)
                        window.open(contactLink);
                    return;
                }
                addToCart.onAddToCart();
            }, size: "lg", disabled: !!(showContact && !contactLink), variant: showContact && !contactLink ? 'disabled' : 'primary', outline: true, block: true }, showContact
            ? contactLink
                ? t('Liên hệ')
                : t('Hết hàng')
            : t('Thêm vào giỏ')))))));
    if (link) {
        return (React.createElement(Link, Object.assign({}, link),
            React.createElement(CustomLink, Object.assign({}, props, { className: className || '' }),
                React.createElement(StyledProductCard$1, Object.assign({ className: "product-card" }, (regionName && {
                    'data-track-content': true,
                    'data-content-region-name': regionName,
                    'data-content-name': props.sku,
                    'data-content-target': 'productDetail',
                })), node))));
    }
    return (React.createElement(StyledProductCard$1, Object.assign({}, (regionName && {
        'data-track-content': true,
        'data-content-region-name': regionName,
        'data-content-name': props.sku,
        'data-content-target': 'productDetail',
    }), props, { className: `${className || ''} product-card` }), node));
};

const FeaturedBanners = (_a) => {
    var _b, _c;
    var { banners, regionName } = _a, props = __rest(_a, ["banners", "regionName"]);
    let bannerHeight = 140;
    if (!banners || !banners.length)
        return null;
    if ((_c = (_b = banners[0]) === null || _b === void 0 ? void 0 : _b.imageSize) === null || _c === void 0 ? void 0 : _c.height) {
        bannerHeight = Number(banners[0].imageSize.height);
    }
    return (React.createElement(Flex, Object.assign({ mx: -2 }, props, {
        'content-visibility': 'auto',
        'contain-intrinsic-size': `${bannerHeight}px`,
    }), banners.map((banner, index) => {
        return (React.createElement(Box, Object.assign({ px: 2, width: 1 / banners.length, key: index }, (regionName && {
            'data-track-content': true,
            'data-content-region-name': regionName,
            'data-content-name': banner.campaignName,
            'data-content-index': index,
            'data-content-target': banner.targetPath,
        })),
            React.createElement(CustomLink$1, { targetPath: banner.targetPath },
                React.createElement(LazyImage, { borderRadius: 8, hover: "zoom", height: bannerHeight, alt: `banner-${index}`, src: banner.image, imageWidth: Math.ceil(1232 / banners.length) }))));
    })));
};

const Wrapper$8 = styled.div `
  background-color: #fff;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
`;
const Title$1 = styled.div `
  font-size: 1rem;
  color: #333333;
  font-weight: bold;
  margin-bottom: 8px;
`;
const Policy = styled.div `
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 8px;
`;
const Content = styled.div `
  margin-left: 8px;
  flex: 1;
`;
const StyledSvgIcon = styled(SvgIcon)(({ theme }) => `
  background: ${theme.brand.primary};

`);
const StyledImage = styled.img `
  width: 24px;
  height: 24px;
`;
const PolicyAndService = ({ information, className, title, type = 'icon' }) => {
    return (React.createElement(Wrapper$8, { className: className },
        React.createElement(Title$1, null, title),
        information.map((infor) => {
            return (React.createElement(Policy, { key: infor.content },
                type === 'icon' && (React.createElement(StyledSvgIcon, { name: infor.icon.name, size: 24 })),
                type === 'url' && React.createElement(StyledImage, { src: infor.icon.name }),
                React.createElement(Content, { dangerouslySetInnerHTML: { __html: infor.content } })));
        })));
};

const reviewMock = {
    items: [
        {
            rating: 4,
            comment: t('chà cà rôn'),
            createdAt: '2020-04-24T03:38:33',
        },
    ],
    statisticVotes: { 1: 0, 2: 0, 3: 0, 4: 1, 5: 0 },
    average: 4,
    totalRating: 1,
};

const StyledButton$2 = styled(Button$1)(({ readOnly }) => `
  padding: 0px;
  pointer-events: ${readOnly ? 'none' : 'auto'};
`);
const RatingStars = ({ stars, onStarClicked, starWidth = 12, readOnly = false, className, }) => {
    const [rating, setRating] = useState(stars);
    const clickStar = (star) => {
        setRating(star);
        onStarClicked && onStarClicked(star);
    };
    useEffect(() => {
        setRating(stars);
    }, [stars]);
    return (React.createElement("div", { className: className }, Array.from(Array(5).keys()).map((i) => {
        return (React.createElement(StyledButton$2, { key: `star-${i}`, icon: {
                name: 'star',
                size: starWidth,
                color: i <= Math.round(rating - 1) ? '#ffd124' : '#d2d2d2',
            }, link: true, block: false, disabled: readOnly, onClick: () => clickStar(i + 1), readOnly: readOnly }));
    })));
};

const StyledBar = styled.div(({ barHeight, barBorder }) => `
  height: ${barHeight}px;
  border-radius: ${barBorder}px;
  background-color: #d2d2d2;
  margin-left: 8px;
  position: relative;
`);
const Progress = styled.div(({ height, border, progressPercentage, theme, color }) => {
    return `
  background-color: ${color || theme.brand.primary};
  position: absolute;
  z-index: 1;
  height: inherit;
  border-radius: ${border}px;
  height: ${height}px;
  width: ${progressPercentage}%;
`;
});
const ProgressBar = ({ progress = 0, barColor, barHeight = 4, barBorder = 2, }) => {
    return (React.createElement(StyledBar, { barHeight: barHeight, barBorder: barBorder },
        React.createElement(Progress, { progressPercentage: progress, color: barColor, height: barHeight, border: barBorder })));
};

const StyledRatingStars = styled(Col) `
  padding-right: 0px;
  min-width: 80px;
`;
const StyledProgressBar = styled(Col)(({ theme }) => `
  color: ${theme.colors.gray600};
  font-size: 12px;
  padding-left: 0px;
`);
const VoteCountCol = styled(Col) `
  padding-top: 6px;
`;
const StyledCol = styled(Col) `
  text-align: right;
`;
const calculatePercent = (use, total) => {
    return (100 * use) / total || 0;
};
const RatingStatistic = ({ stars, voteCount, totalRating, }) => {
    return (React.createElement("div", null,
        React.createElement(Row, null,
            React.createElement(StyledRatingStars, { col: 3.5 },
                React.createElement(RatingStars, { stars: stars, readOnly: true })),
            React.createElement(StyledProgressBar, { col: 8.5 },
                React.createElement(Row, null,
                    React.createElement(VoteCountCol, { col: 9.5 },
                        React.createElement(ProgressBar, { barHeight: 4, progress: calculatePercent(voteCount, totalRating), barColor: "rgb(95, 231, 255)" })),
                    React.createElement(StyledCol, { col: 2.5 },
                        React.createElement("span", null, voteCount)))))));
};

const Average = styled.div `
  font-weight: 500;
  font-size: 36px;
`;
const Total = styled.span `
  color: #b4b4b4;
`;
const TotalComment = styled.div `
  font-size: 14px;
  margin-bottom: 24px;
  color: #848788;
`;
const VoteCount = styled.span `
  color: #333333;
`;
const OverallRating = ({ average, totalRating, statisticVotes, }) => {
    return (React.createElement(React.Fragment, null,
        average ? (React.createElement(React.Fragment, null,
            React.createElement(Average, null,
                average,
                React.createElement(Total, null, "/5")),
            React.createElement(RatingStars, { stars: average, readOnly: true, starWidth: 20 }),
            React.createElement(TotalComment, null,
                t('Trung bình của:'),
                React.createElement(VoteCount, null,
                    ' ',
                    totalRating,
                    t('nhận xét'))))) : (React.createElement("div", { className: "mb26" }, t('Chưa có đánh giá nào'))),
        React.createElement(React.Fragment, null, Array.from(Array(5).keys())
            .reverse()
            .map((i) => {
            return (React.createElement(RatingStatistic, { key: `rating-statistic-${i}`, stars: i + 1, voteCount: statisticVotes[i + 1], totalRating: totalRating }));
        }))));
};

const Divider$4 = styled.hr `
  margin: 0 16px;
  width: 90%;
`;
const Form = styled.div `
  padding: 15px;
  margin: 0 auto;
  margin-top: 30px;
  border-radius: 10px;
  background-color: #f6f6f6;
  width: 100%;
`;
const CommentTitle = styled.div `
  height: 19px;
  font-size: 16px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: -0.11px;
  color: #000000;
  margin-bottom: 13px;
`;
const InputGroup = styled.div `
  display: block;
  margin-top: 15px;
`;
const FormLabel = styled.label `
  width: 200px;
`;
const CommentBox = styled.textarea `
  flex: 1;
  border-radius: 3px;
  padding: 0;
  outline: none;
  padding-left: 10px;
  padding-right: 10px;
  padding-top: 5px;
  font-size: 14px;
  background-color: white;
  line-height: 1.5;
  &::-webkit-input-placeholder {
    font-size: 12px;
    color: #999;
  }
  border: solid 1px #e0e0e0;
  width: 100%;
  resize: none;
`;
const StyledButton$1 = styled(Button$1)(({ theme }) => `
  margin-top: 24px;
  width: 100%;
  background: ${theme.brand.primary};
  color: #fff;
  padding: 12px;
  &:hover {
    color: ${theme.brand.primary};
    background: #fff;
  }
  text-align: center;
  justify-content: center;
`);
const ReviewForm = ({ onSendRating, }) => {
    const [rating, setRating] = useState(-1);
    const [comment, setComment] = useState('');
    const handleAddComment = (e) => {
        setComment(e.target.value);
    };
    return (React.createElement(Form, null,
        React.createElement(CommentTitle, null, t('Nhận xét của bạn')),
        React.createElement(Divider$4, null),
        React.createElement(InputGroup, null,
            React.createElement(FormLabel, null, t('1. Đánh giá về sản phẩm:')),
            React.createElement(RatingStars, { stars: -1, onStarClicked: (star) => setRating(star), starWidth: 20 })),
        React.createElement(InputGroup, null,
            React.createElement(FormLabel, null, t('2. Viết nhận xét:')),
            React.createElement(CommentBox, { value: comment, onChange: handleAddComment, rows: 5, placeholder: t('Nhận xét về sản phẩm') }, comment)),
        React.createElement(StyledButton$1, { onClick: () => onSendRating(rating, comment) },
            React.createElement("strong", null, t('Gửi nhận xét')))));
};

const FormattedTime = ({ time, }) => {
    return React.createElement("span", null, format(new Date(time.dateTime), time.timeFormat));
};

const RateItem = styled.div `
  display: flex;
  padding: 24px;
`;
const UserInfo = styled.div `
  display: flex;
  width: 25%;
`;
const CustomerName = styled.div `
  font-size: 1rem;
  font-weight: 500;
`;
const RatingTime = styled.div `
  color: #848788;
`;
const ReviewContent = styled.div `
  line-height: 20px;
  width: 75%;
`;
const StyledButtonIcon = styled(Button$1) `
  width: 80px;
  .title {
    font-size: 12px;
  }
`;
const Divider$3 = styled.hr `
  width: 90%;
  margin: 0 auto;
`;
const ReviewLine = ({ avatar, userName, title, comment, rating, time, }) => {
    return (React.createElement(React.Fragment, null,
        React.createElement(RateItem, null,
            React.createElement(UserInfo, null,
                !avatar ? (React.createElement(StyledButtonIcon, { icon: { name: 'account', size: 32 }, link: true })) : (React.createElement("img", { src: avatar, alt: "avatar" })),
                React.createElement("div", { className: "ml8" },
                    React.createElement(CustomerName, null, userName),
                    time && (React.createElement(RatingTime, null,
                        React.createElement(FormattedTime, { time: { dateTime: time, timeFormat: 'dd/MM/yyyy HH:mm:ss' } }))))),
            React.createElement(ReviewContent, null,
                React.createElement(RatingStars, { stars: rating, readOnly: true }),
                React.createElement("div", null, title),
                React.createElement("div", null, comment))),
        React.createElement(Divider$3, null)));
};

const ReviewWrapper = styled.div ``;
const StyledContainer = styled.div `
  border-top: 1px solid rgb(224, 224, 224);
  padding: 16px;
`;
const StyledPaginationBlock = styled.div `
  position: relative;
  width: fit-content;
  margin-left: auto;
  margin-right: 20px;
  padding-bottom: 20px;
`;
const ProductReview = ({ review = reviewMock, showForm = false, onSendRating, ratingPage, onPageChanged, pageLimit = 10, }) => {
    return (React.createElement(Card, null,
        React.createElement(ReviewWrapper, null,
            React.createElement(BlockHeader, { title: t('Nhận xét và đánh giá') }),
            React.createElement(StyledContainer, null,
                React.createElement(Row, null,
                    React.createElement(Col, { col: 3 },
                        React.createElement(OverallRating, { average: review.average, totalRating: review.totalRating, statisticVotes: review.statisticVotes }),
                        showForm && (React.createElement(ReviewForm, { onSendRating: (rating, comment) => onSendRating(rating, comment) }))),
                    React.createElement(Col, { col: 9 }, review &&
                        review.totalRating > 0 &&
                        review.items.map((item) => {
                            return (React.createElement(ReviewLine, { key: item.createdAt, rating: item.rating, comment: item.comment, time: item.createdAt }));
                        })))),
            review && review.totalRating > 0 && (React.createElement(StyledPaginationBlock, null,
                React.createElement(Pagination, { totalRecords: review.totalRating, pageLimit: pageLimit, onPageChanged: (page) => onPageChanged(page), currentPage: ratingPage }))))));
};

const Wrapper$7 = styled.div `
  .swiper-slide {
    max-width: ${(props) => 100 / props.slidesPerView + '%'};
  }
`;
const StyledSwiper = styled(Swiper)(({ hideArrowButton }) => `
    ${hideArrowButton
    ? `& .swiper-button-prev, .swiper-button-next {
                display: none !important;
              }`
    : ''};
  `);
const ProductsCarousel = (_a) => {
    var { products = [], slidesPerView = 4, spaceBetween = 0, className, regionName } = _a, props = __rest(_a, ["products", "slidesPerView", "spaceBetween", "className", "regionName"]);
    if (products.length === 0) {
        return null;
    }
    return (React.createElement(Wrapper$7, { slidesPerView: slidesPerView, className: className },
        React.createElement(StyledSwiper, Object.assign({ spaceBetween: spaceBetween, slidesPerView: slidesPerView, slidesPerGroup: slidesPerView, hideArrowButton: (products === null || products === void 0 ? void 0 : products.length) <= slidesPerView }, props), products.map((product, sku) => (React.createElement(ProductCard, Object.assign({ key: sku }, product, { regionName: regionName })))))));
};

const Logo = (_a) => {
    var { variant = 'full', logoFull, logoLite, alt } = _a, props = __rest(_a, ["variant", "logoFull", "logoLite", "alt"]);
    return (React.createElement(Image, Object.assign({ src: variant === 'full' ? logoFull : logoLite, alt: alt }, props)));
};

const StyledProductsGrid = styled.div `
  display: flex;
  flex-wrap: wrap;
`;
const StyledProductCard = styled(ProductCard) `
  box-sizing: border-box;
  width: ${(props) => 100 / props.productsPerRow}%;
  border-radius: 0.5rem;
`;
const Line$2 = styled.div `
  width: 100%;
  height: 1px;
  margin-top: 16px;
  margin-bottom: 16px;
  background-color: #e0e0e0;
`;
const ProductsGrid = ({ products = [], productsPerRow = 5, regionName, }) => {
    return (React.createElement(Card, null,
        React.createElement(StyledProductsGrid, null, products.map((product, index) => (React.createElement(React.Fragment, { key: index },
            React.createElement(StyledProductCard, Object.assign({ productsPerRow: productsPerRow, key: product.sku }, product, { regionName: regionName })),
            index % productsPerRow === productsPerRow - 1 &&
                index !== products.length - 1 && React.createElement(Line$2, null)))))));
};

const SearchSuggestion = styled.div `
  position: absolute;
  background: #fff;
  width: 100%;
  border-radius: 8px;
  box-shadow: 0 4px 6px 0 rgba(0, 0, 0, 0.1);
  padding: 0.5rem 0.8rem;
  display: ${(props) => props.show ? 'block' : 'none'};
`;
const StyledListTile$1 = styled(ListTile) `
  border-radius: 8px;
`;
const StyledSearchTitle = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 4px 0;
`;
const StyledRecommendedKeyword = styled.div `
  margin: 8px 0 4px;
`;
const ListTerm = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  column-gap: 8px;
  row-gap: 8px;
`;
const TermItem = styled.div `
  display: flex;
  align-items: center;
  height: 40px;
  line-height: 40px;
  padding: 0 8px;
  border-radius: 4px;
  border: 1px solid #e4e5f0;
  font-size: 13px;
  cursor: pointer;
  &:hover {
    background-color: #f5f5f5;
  }
`;
const StyledSearchIcon = styled(SvgIcon) `
  margin-right: 8px;
  min-width: 20px;
`;
const SearchSuggestions = ({ suggestions, selected = null, onHoverItem, onClick, show, popularKeywords = [], showPopularKeywords = false, }) => {
    const renderRecommendedKeyword = () => {
        if (typeof window !== 'undefined' &&
            showPopularKeywords &&
            popularKeywords.length > 0)
            return (React.createElement(StyledRecommendedKeyword, null,
                React.createElement(StyledSearchTitle, null,
                    React.createElement(Text$6, { fontWeight: "500", textTransform: "uppercase", color: "#82869E" }, t('Từ khóa phổ biến'))),
                React.createElement(ListTerm, null, popularKeywords.map((term, index) => {
                    return (React.createElement(TermItem, { "data-track-content": true, "data-content-region-name": "popularKeyword", "data-content-name": term, "data-content-index": index, onClick: () => onClick(term), key: `popularKeyword${index}` },
                        React.createElement(StyledSearchIcon, { name: "search", size: 20 }),
                        React.createElement(Text$6, { maxLines: 1 }, term)));
                }))));
    };
    return (React.createElement(SearchSuggestion, { show: show &&
            (suggestions.length > 0 ||
                (popularKeywords.length > 0 && showPopularKeywords)) },
        suggestions.length > 0 && (React.createElement(React.Fragment, null,
            suggestions.every((s) => s.icon === 'time-outline') && (React.createElement(StyledSearchTitle, null,
                React.createElement(Text$6, { fontWeight: "500", textTransform: "uppercase", color: "#82869E" }, t('Lịch sử tìm kiếm')))),
            suggestions.map((suggestionItem, index) => {
                const regionName = suggestionItem.icon === 'time-outline'
                    ? 'recentlySearch'
                    : 'popularKeyword';
                return (React.createElement(StyledListTile$1, { selected: index === selected, key: `recentlySearch${index}`, leading: React.createElement(SvgIcon, { name: suggestionItem.icon, size: 20 }), title: suggestionItem.keyword, onClick: () => onClick(suggestionItem.keyword), "data-track-content": true, "data-content-region-name": regionName, "data-content-name": suggestionItem.keyword, "data-content-index": index, onMouseEnter: () => onHoverItem(index), type: "search-suggestion" }));
            }))),
        renderRecommendedKeyword()));
};

const Wrapper$6 = styled.div(space, `
    white-space: nowrap;
    display: flex;
  `);
const BreadcrumbBlock = styled.div `
  height: 32px;
  width: fit-content;
  align-items: center;
  display: flex;
  justify-content: center;
  white-space: nowrap;
`;
const StyledButton = styled(Button$1)(({ isDisable, color, outline }) => `
  padding: ${outline ? '8px' : '4px'};
  color: ${isDisable ? 'rgba(0,0,0,.38)' : color};
  text-decoration: none;
  background-color: #FFF;
  pointer-events: ${isDisable ? 'none' : 'auto'};
  border: ${outline ? '1px solid #e0e0e0' : 'none'};
  border-radius: 9999px;
  --button-icon-color: ${color};
  &:hover {
    color: ${color};
    background-color: #FFF;
    --button-icon-color: ${color};
  }
   `);
const Divider$2 = styled.span `
  margin: 0 8px;
  color: #9b9b9b;
  font-size: inherit;
`;
const Breadcrumb = (_a) => {
    var { items, divider, color = '#848788', outline } = _a, props = __rest(_a, ["items", "divider", "color", "outline"]);
    return (React.createElement(Wrapper$6, Object.assign({}, props), items.map((breadcrumb, index) => {
        return (React.createElement(BreadcrumbBlock, { key: index },
            React.createElement(StyledButton, { icon: breadcrumb.icon, link: true, outline: outline, color: color, isDisable: breadcrumb.disabled, href: breadcrumb.href, tag: "a" }, breadcrumb.text),
            index !== items.length - 1 && React.createElement(Divider$2, null, divider)));
    })));
};

const Table$1 = styled.table `
  width: 100%;
  td {
    line-height: 1;
  }
`;
const Line$1 = styled.td(({ color, first, align }) => `
    color: ${color};
    padding: 0;
    padding-top: ${first ? '0px' : '0.85rem'};
    text-align: ${align};
    vertical-align: top;
  `);
const Price$2 = styled.td(({ first }) => `
    font-weight: 700;
    font-size: 14px;
    padding: 0;
    text-align: right;
    padding-top: ${first ? '0px' : '0.85rem'};
    vertical-align: top;
  `);
const FinalPrice$1 = styled.td `
  color: #eb2101;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1;
  text-align: right;
  vertical-align: bottom;
  padding: 0;
`;
const Text$5 = styled.div `
  font-size: 14px;
  color: #848788;
  margin-top: 4px;
  text-align: center;
`;
const Wrapper$5 = styled.div `
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;
const Divider$1 = styled.div `
  width: 100%;
  border: 1px dashed #e4e5f0;
  height: 2px;
  margin-top: 15px;
`;
const Invoices = ({ invoices, align = 'left', textColor = '#848788', vat, className, hideShippingFee, infoText, }) => {
    return (React.createElement(Wrapper$5, { className: className },
        React.createElement(Table$1, null,
            React.createElement("tbody", null, invoices.map(({ text, price, notFormat, tag, separator }, index) => (React.createElement(React.Fragment, null,
                separator && (React.createElement("tr", null,
                    React.createElement("td", { colSpan: 2 },
                        React.createElement(Divider$1, null)))),
                React.createElement("tr", { key: index },
                    React.createElement(Line$1, { color: textColor, first: !index, align: align },
                        text,
                        ' ',
                        !!tag && (React.createElement(Tag, { variant: "primary", outline: true }, tag))),
                    index !== invoices.length - 1 ? (React.createElement(Price$2, { first: !index }, notFormat ? (price) : (React.createElement(Currency, { value: price, space: true, underline: true })))) : (React.createElement(FinalPrice$1, null,
                        React.createElement(Currency, { value: price, space: true }))))))))),
        vat && (React.createElement(Text$5, null, t('(Đã bao gồm VAT{{var0}})', {
            var0: hideShippingFee
                ? ', Chiết Khấu và phí giao hàng nếu có'
                : '',
        }))),
        !vat && hideShippingFee && (React.createElement(Text$5, null, t('(Đã bao gồm phí giao hàng)'))),
        !!infoText && React.createElement(Text$5, null, infoText)));
};

const Table = styled.table `
  width: 100%;
  td {
    line-height: 1;
  }
`;
const Line = styled.td(({ color, first, align }) => `
    color: ${color};
    padding: 0;
    padding-top: ${first ? '0px' : '0.85rem'};
    text-align: ${align};
    vertical-align: top;
  `);
const Price$1 = styled.td(({ first }) => `
    font-weight: 700;
    font-size: 14px;
    padding: 0;
    text-align: right;
    padding-top: ${first ? '0px' : '0.85rem'};
    vertical-align: top;
  `);
const FinalPrice = styled.td `
  color: #eb2101;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1;
  text-align: right;
  vertical-align: bottom;
  padding: 0;
`;
const Text$4 = styled.div `
  font-size: 14px;
  color: #848788;
  margin-top: 4px;
  text-align: center;
`;
const Wrapper$4 = styled.div `
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;
var InvoiceSectionKey;
(function (InvoiceSectionKey) {
    InvoiceSectionKey[InvoiceSectionKey["SubTotal"] = 1] = "SubTotal";
    InvoiceSectionKey[InvoiceSectionKey["AddedPrice"] = 2] = "AddedPrice";
    InvoiceSectionKey[InvoiceSectionKey["ShippingFee"] = 3] = "ShippingFee";
    InvoiceSectionKey[InvoiceSectionKey["OtherFees"] = 4] = "OtherFees";
    InvoiceSectionKey[InvoiceSectionKey["Discount"] = 5] = "Discount";
    InvoiceSectionKey[InvoiceSectionKey["GrandTotal"] = 6] = "GrandTotal";
    InvoiceSectionKey[InvoiceSectionKey["ShippingFeeDiscount"] = 7] = "ShippingFeeDiscount";
})(InvoiceSectionKey || (InvoiceSectionKey = {}));
const OrderDetailInvoices = ({ orderDetail, align = 'left', textColor = '#848788', vat, className, infoText, transformSections, }) => {
    var _a;
    let invoices = [];
    if (orderDetail) {
        const { grandTotal, shippingFee, subTotal, unitAdd, serviceFee, promotions, } = orderDetail;
        const discount = (promotions === null || promotions === void 0 ? void 0 : promotions.reduce((total, promo) => {
            if (['order', 'part_order'].includes(promo === null || promo === void 0 ? void 0 : promo.applyType)) {
                return total + ((promo === null || promo === void 0 ? void 0 : promo.discount) || 0);
            }
            return total;
        }, 0)) || 0;
        const shippingFeeDiscount = (promotions === null || promotions === void 0 ? void 0 : promotions.reduce((total, promo) => {
            if ((promo === null || promo === void 0 ? void 0 : promo.applyType) === 'service_fee') {
                return total + ((promo === null || promo === void 0 ? void 0 : promo.discount) || 0);
            }
            return total;
        }, 0)) || 0;
        invoices = [
            {
                key: InvoiceSectionKey.SubTotal,
                text: t('Tổng tạm tính'),
                price: subTotal,
            },
            unitAdd && {
                key: InvoiceSectionKey.AddedPrice,
                text: t('Tổng nâng giá'),
                price: unitAdd,
            },
            shippingFee !== undefined && {
                key: InvoiceSectionKey.ShippingFee,
                text: t('Phí vận chuyển'),
                price: `${shippingFee || 0}`,
            },
            !!shippingFeeDiscount &&
                !!shippingFee && {
                key: InvoiceSectionKey.ShippingFeeDiscount,
                text: t('Giảm giá phí vận chuyển'),
                price: shippingFeeDiscount > shippingFee
                    ? -shippingFee
                    : -shippingFeeDiscount,
            },
            !!((_a = serviceFee === null || serviceFee === void 0 ? void 0 : serviceFee.optional) === null || _a === void 0 ? void 0 : _a.length) && {
                key: InvoiceSectionKey.OtherFees,
                children: serviceFee.optional.map((fee) => ({
                    text: fee.name,
                    price: fee.rowTotal,
                })),
            },
            {
                key: InvoiceSectionKey.Discount,
                text: t('Giảm giá'),
                price: -discount,
            },
            {
                key: InvoiceSectionKey.GrandTotal,
                text: t('Thành tiền'),
                price: grandTotal,
            },
        ].filter((invoice) => !!invoice);
        invoices = transformSections ? transformSections(invoices) : invoices;
        invoices = invoices.map((line, index) => {
            if (line.children) {
                return (React.createElement(React.Fragment, { key: line.key }, line.children.map((fee, feeIndex) => {
                    return (React.createElement("tr", { key: 'fee_' + feeIndex },
                        React.createElement(Line, { color: textColor, align: align }, fee.text),
                        React.createElement(Price$1, null,
                            React.createElement(Currency, { value: fee.price, space: true, underline: true }))));
                })));
            }
            else {
                return (React.createElement("tr", { key: line.key },
                    React.createElement(Line, { color: textColor, first: !index, align: align }, line.text),
                    index !== invoices.length - 1 ? (React.createElement(Price$1, { first: !index }, line.noFormat ? (line.price) : (React.createElement(Currency, { value: line.price, space: true, underline: true })))) : (React.createElement(FinalPrice, null,
                        React.createElement(Currency, { value: line.price, space: true })))));
            }
        });
    }
    return (React.createElement(Wrapper$4, { className: className },
        React.createElement(Table, null,
            React.createElement("tbody", null, invoices)),
        vat && (React.createElement(Text$4, null, t('(Đã bao gồm VAT{{var0}})', {
            var0: (orderDetail === null || orderDetail === void 0 ? void 0 : orderDetail.shippingFee) === undefined
                ? ', Chiết Khấu và phí giao hàng nếu có'
                : '',
        }))),
        !vat && (orderDetail === null || orderDetail === void 0 ? void 0 : orderDetail.shippingFee) === undefined && (React.createElement(Text$4, null, t('(Đã bao gồm phí giao hàng)'))),
        !!infoText && React.createElement(Text$4, null, infoText)));
};

const List = styled.ul `
  padding: 0;
  margin: 0;
  list-style: none;
`;
const StyledListTile = styled(ListTile)(({ theme, active }) => `
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 0;
    margin: 8px 0;
    background: unset;

    ${active
    ? `
      font-weight: 700 !important;
      color: ${theme.brand.primary} !important;
      > :not(.noti) path {
        fill: ${theme.brand.primary};
      }
      > .noti path{
        stroke: ${theme.brand.primary};
      }
    `
    : `
    &:hover, .active {
      font-weight: 700 !important;
      color: ${theme.brand.primary} !important;
      > :not(.noti) path {
        fill: ${theme.brand.primary};
      }
      > .noti path{
        stroke: ${theme.brand.primary};
      }
    }`}
  `);
const Divider = styled.li `
  background-color: #e0e0e0;
  width: 100%;
  height: 1px;
  list-style: none;
`;
const StyledA = styled.a `
  text-decoration: none;
`;
const SideNav = (_a) => {
    var { navItems, activeHref } = _a, props = __rest(_a, ["navItems", "activeHref"]);
    return (React.createElement(List, Object.assign({}, props), navItems.map(({ icon, text, href }, index) => {
        const node = (React.createElement(StyledListTile, { leading: icon, title: text, active: href === activeHref || (index && activeHref.startsWith(href)) }));
        return (React.createElement(React.Fragment, { key: index },
            href ? (React.createElement(Link, { href: href },
                React.createElement(StyledA, null, node))) : (node),
            index === navItems.length - 1 && React.createElement(Divider, null)));
    })));
};

const Wrapper$3 = styled.div `
  display: flex;
`;
const Info = styled.div `
  margin-left: 0.5rem;
`;
const Fullname = styled.h5 `
  font-size: 1rem;
`;
const Title = styled.h6 `
  margin-bottom: 0;
  font-weight: 400;
  font-size: 0.8rem;
`;
const UserCard = ({ name, picture, }) => (React.createElement(Wrapper$3, null,
    React.createElement("div", null, picture ? (React.createElement(LazyImage, { src: picture, height: 44, width: 44 })) : (React.createElement(SvgIcon, { name: "account", size: 32 }))),
    React.createElement(Info, null,
        React.createElement(Title, null, t('Tài khoản của')),
        React.createElement(Fullname, null, name))));

const Wrapper$2 = styled.div `
  position: relative;
  border-radius: 0.5rem;
  background: #fff;
  height: ${(props) => props.height + 'px'};
  user-select: none;
`;
const StyledLogoLoading = styled(LogoLoading) `
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
`;
const PreLoad = (_a) => {
    var { height, isLoading, hideLogo = false, logo } = _a, props = __rest(_a, ["height", "isLoading", "hideLogo", "logo"]);
    if (!isLoading)
        return null;
    return (React.createElement(Wrapper$2, Object.assign({}, props, { height: height }), !hideLogo && React.createElement(StyledLogoLoading, { logo: logo })));
};

const Wrapper$1 = styled.div `
  border-radius: 0.5rem;
  overflow: hidden;
`;
const MiniSlides = (_a) => {
    var { banners = [], isLoading = false, regionName, height = 140, width = 1232 } = _a, props = __rest(_a, ["banners", "isLoading", "regionName", "height", "width"]);
    if (isLoading)
        return React.createElement(PreLoad, { height: height, isLoading: isLoading });
    if (!isLoading && !banners.length)
        return null;
    return (React.createElement(Wrapper$1, Object.assign({}, props),
        React.createElement(Swiper, { autoplay: {
                delay: 2500,
            }, slidesPerView: 1, loop: true, hasNavigation: true }, banners.map((banner, index) => {
            return (React.createElement("div", { key: index, className: "swiper-slide", "data-track-content": true, "data-content-region-name": regionName || 'topBanner', "data-content-name": banner.campaignName, "data-content-index": index, "data-content-target": banner.targetPath },
                React.createElement(CustomLink$1, { targetPath: banner.targetPath },
                    React.createElement(LazyImage, { borderRadius: 8, height: height, src: banner.image, alt: banner.campaignName, width: width, imageWidth: width, hover: "zoom", cover: true }))));
        }))));
};

const StyledProductCoupon = styled.div `
  display: flex;
  justify-content: space-between;
`;
const CouponCode = styled.div ``;
const CouponDiscount = styled.div((props) => `
    display: flex;
    flex-direction: ${props.reverse ? 'column-reverse' : 'column'};
    text-align: right;
    margin-left: 0.5rem;
  `);
const Text$3 = styled.div `
  font-size: 11px;
`;
const Highlight$1 = styled.div(({ theme }) => `
  font-size: 0.9rem;
  font-weight: 500;
  color: ${theme.colors.red}
  `);
const ProductCoupon = ({ tag, detail, className, }) => (React.createElement(StyledProductCoupon, { className: className },
    React.createElement(CouponCode, null,
        React.createElement(Text$3, null, t('Nhập mã')),
        React.createElement(Tag, Object.assign({}, tag))),
    React.createElement(CouponDiscount, { reverse: detail.reverse },
        React.createElement(Text$3, null, detail.text),
        React.createElement(Highlight$1, null, detail.highlight))));

const Price = styled.div `
  text-align: right;
  margin-left: 0.5rem;
`;
const Text$2 = styled.div `
  font-size: 11px;
`;
const Highlight = styled.div(({ theme }) => `
  font-size: 0.9rem;
  font-weight: 500;
  color: ${theme.colors.red}
  `);
const StyledProductDiscount = styled.div `
  display: flex;
  justify-content: space-between;
`;
const ProductDiscount = (_a) => {
    var { discount, price } = _a, props = __rest(_a, ["discount", "price"]);
    return (React.createElement(StyledProductDiscount, Object.assign({}, props),
        React.createElement("div", null,
            React.createElement(Text$2, null, t('Giảm ngay')),
            React.createElement(Highlight, null, discount)),
        React.createElement(Price, null,
            React.createElement(Text$2, null,
                t('Chỉ còn'),
                " "),
            React.createElement(Highlight, null, price))));
};

var messengerFull = "https://shopfront-cdn.tekoapis.com/static/3ad4c9a97ab98ff3.png";

var messenger = "https://shopfront-cdn.tekoapis.com/static/a8e347d31db4d701.png";

const Wrapper = styled.a `
  z-index: 1030;
  position: fixed;
  right: 2.5rem;
  bottom: 5.6rem;
  cursor: pointer;
  @media (min-width: 1440px) {
    .fb-chat-full {
      display: block;
    }
    .fb-chat {
      display: none;
    }
  }
  @media (max-width: 1440px) {
    .fb-chat-full {
      display: none;
    }
    .fb-chat {
      display: block;
    }
  }
  @media print {
    display: none;
  }
`;
const Messenger = ({ url, }) => {
    return (React.createElement(Wrapper, { href: url, target: "_blank", rel: "noreferrer", "data-track-content": true, "data-content-region-name": "quickAction", "data-content-name": "fbMessengerIcon" },
        React.createElement("img", { className: "fb-chat-full", src: messengerFull, alt: "fb-chat" }),
        React.createElement("img", { className: "fb-chat", src: messenger, alt: "fb-chat" })));
};

const GoogleButton = styled.button `
  background-color: rgb(220, 78, 66);
  width: 100%;
  padding: 10px;
  border: none;
  color: white;
  margin-bottom: 16px;
  position: relative;
  &:hover {
    background-color: rgb(220, 78, 66);
    opacity: 0.8;
  }
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  cursor: pointer;
  > span {
    position: absolute;
    left: 10px;
  }
`;
const GoogleIcon = styled(SvgIcon) `
  margin-right: 4px;
`;
const GoogleLogin = ({ onClick, clientId, text, }) => {
    const handleLoad = async () => {
        gapi.load('auth2', () => {
            gapi.auth2.init({ client_id: clientId });
        });
    };
    const handleClick = () => {
        if (typeof gapi !== 'undefined' && gapi.auth2) {
            onClick(gapi.auth2.getAuthInstance());
        }
    };
    useEffect(() => {
        if (typeof gapi === 'undefined') {
            const myScript = document.createElement('script');
            myScript.src = 'https://apis.google.com/js/platform.js';
            myScript.defer = true;
            myScript.onload = handleLoad;
            document.head.appendChild(myScript);
        }
    }, []);
    return (React.createElement(GoogleButton, { "data-track-content": true, "data-content-region-name": "loginMethod", "data-content-name": "Google", onClick: handleClick },
        React.createElement(GoogleIcon, { name: "logo-google", size: 20, color: "white" }),
        text));
};

const FacebookButton = styled(Button$1) `
  background-color: rgb(45, 136, 255);
  width: 100%;
  padding: 12px;
  border: none;
  color: white;
  margin-bottom: 16px;
  &:hover {
    background-color: rgb(45, 136, 255);
    opacity: 0.8;
  }
  > span {
    position: absolute;
    left: 10px;
  }
`;
const FacebookLogin = ({ onClick, appId, text, }) => {
    const handleLoad = () => {
        if (typeof FB !== 'undefined') {
            FB.init({
                appId,
                cookie: true,
                xfbml: true,
                version: 'v8.0',
            });
            FB.AppEvents.logPageView();
        }
    };
    const handleClick = () => {
        if (typeof FB !== 'undefined') {
            FB.getLoginStatus((response) => {
                if (response.status !== 'connected') {
                    FB.login((loginResponse) => {
                        onClick(loginResponse.authResponse);
                    });
                }
                else {
                    onClick(response.authResponse);
                }
            });
        }
    };
    useEffect(() => {
        if (typeof FB === 'undefined') {
            const myScript = document.createElement('script');
            myScript.src = 'https://connect.facebook.net/en_US/sdk.js';
            myScript.crossOrigin = 'anonymous';
            myScript.defer = true;
            myScript.onload = handleLoad;
            document.head.appendChild(myScript);
        }
    }, []);
    return (React.createElement(FacebookButton, { icon: { name: 'logo-facebook', size: 20, color: 'white' }, onClick: handleClick, "data-track-content": true, "data-content-region-name": "loginMethod", "data-content-name": "Facebook" }, text));
};

const PhoneButton = styled(Button$1) `
  border: 1px solid ${(props) => props.theme.brand.primary};
  background-color: ${(props) => props.theme.colors.white};
  width: 100%;
  padding: 11px;
  margin-top: 16px;
  margin-bottom: 16px;
  &:hover {
    background-color: ${(props) => props.theme.colors.white};
    opacity: 0.8;
    border: 1px solid ${(props) => props.theme.brand.primary};
  }
  > span {
    position: absolute;
    left: 10px;
  }
`;
const StyledPhoneInput = styled(Input) `
  margin-top: 20px;
`;
const ResendOtpBtn = styled(Button$1) `
  font-size: 14px;
  line-height: 20px;
  margin-top: 40px;
  width: 100%;
`;
const SendOtpBtn = styled(Button$1) `
  height: 32px;
  padding: 0 30px;
  margin-bottom: 0.5rem;
`;
const PhoneLogin = ({ onOTPChange, onSendOTP, onResendOTP, onPhoneChange, phone, isSentOTP, showPhoneLogin, onClick, totalCountDown, hideExpandTriggerButton, canEditPhone = true, }) => {
    const [countDownTime, setCountDownTime] = useState(totalCountDown);
    const [intervalId, setIntervalId] = useState(null);
    const handlePhoneChange = (e) => {
        onPhoneChange(e.target.value);
    };
    const handleSendOTP = () => {
        onSendOTP(t('Đã gửi mã OTP'));
    };
    const checkEnterOnPhoneInput = (e) => {
        if (e.which === 13 || e.keyCode === 13) {
            handleSendOTP();
        }
    };
    const handleResendOtp = () => {
        if (countDownTime === 0)
            onResendOTP(t('Đã gửi lại mã OTP'));
    };
    const handleOTPChange = (otp) => {
        if (otp.length === 6) {
            onOTPChange(otp);
        }
    };
    const startTimer = () => setInterval(() => {
        setCountDownTime((prev) => (prev === 0 ? prev : prev - 1));
    }, 1000);
    useEffect(() => {
        if (countDownTime === totalCountDown && isSentOTP) {
            setIntervalId(startTimer());
        }
        else if (countDownTime === 0) {
            clearInterval(intervalId);
        }
    }, [isSentOTP, totalCountDown]);
    return (React.createElement(React.Fragment, null,
        !hideExpandTriggerButton && (React.createElement(PhoneButton, { icon: { name: 'call', size: 20, color: '#eb1f3a' }, onClick: onClick, "data-track-content": true, "data-content-region-name": "loginMethod", "data-content-name": "PhoneNumber" }, t('Tiếp tục với số điện thoại'))),
        showPhoneLogin &&
            (!isSentOTP ? (React.createElement("div", null,
                React.createElement("div", null, t('src.Common.PhoneLogin.PhoneLogin.0')),
                React.createElement(StyledPhoneInput, { value: phone, onChange: handlePhoneChange, onKeyPress: checkEnterOnPhoneInput, placeholder: t('Nhập số điện thoại'), disabled: !canEditPhone }),
                React.createElement(SendOtpBtn, { marginTop: 16, variant: "primary", onClick: handleSendOTP }, t('Gửi')))) : (React.createElement(React.Fragment, null,
                React.createElement("div", null, t('Vui lòng nhập số OTP đã được gửi về số điện thoại {{phone}} để thực hiện đăng nhập', { phone })),
                React.createElement(OtpInput, { onChange: handleOTPChange }),
                React.createElement(ResendOtpBtn, { disabled: countDownTime > 0, onClick: handleResendOtp, link: true }, countDownTime === 0
                    ? t('Gửi lại mã')
                    : t('Gửi lại mã sau {{countDownTime}}s', {
                        countDownTime: countDownTime,
                    })))))));
};

const HorizontalSelectList = styled.ul `
  display: flex;
  background-color: white;
  padding: 0;
  margin: 0;
  border-radius: 5px;
  overflow: hidden;
  box-shadow: 5px 5px 20px 0px rgba(230, 230, 230, 1);
`;
const HorizontalSelectItem = styled.li(({ selected, theme }) => `
  list-style-type: none;
  padding: 0.6rem;
  font-size: 0.95rem;
  color: ${selected ? theme.brand.primary : '#999'};
  background-color: ${selected ? '#f9f9f9' : 'white'};

  cursor: pointer;
  transition-duration: 0.1s;

  &:hover {
    background-color: #f9f9f9;
    color: ${theme.brand.primary};
  }
  `);
const HorizontalSelect = ({ items, onChange, selected }) => {
    return (React.createElement(HorizontalSelectList, null, items.map(({ value, content }, index) => (React.createElement(HorizontalSelectItem, { key: index, onClick: () => onChange(value), selected: value === selected }, content)))));
};

const GroupTitle = styled.p `
  font-size: 0.8125rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #434657;
  line-height: 1.125rem;
`;
const Text$1 = styled.p `
  font-size: 0.8125rem;
  font-weight: 400;
  color: #434657;
  margin: 0;
  margin-left: 0.5rem;
  cursor: pointer;
  line-height: 1.125rem;
  flex: 1;
`;
const Group = styled.div `
  &:not(:last-child) {
    margin-bottom: 1.5rem;
  }
`;
const Option = styled.div `
  display: flex;
  align-items: center;
  &:not(:last-child) {
    margin-bottom: 1rem;
  }
`;
const StyledCheckbox = styled(Checkbox) `
  margin: 0;
  border-radius: 2px;
`;
const GroupDescription = styled.p `
  margin: 0;
  font-size: 0.8125rem;
  color: #82869e;
  font-weight: 400;
  margin-top: -8px;
  margin-bottom: 1rem;
`;
const Alternative = styled.textarea `
  margin-top: 0.25rem;
  width: 100%;
  border-color: #e4e5f0;
  border-radius: 0.25rem;
  padding: 0.5rem;
  resize: none;
  color: #434657;
`;
const AlternativeTitle = styled.span `
  display: inline-block;
  font-size: 0.8125rem;
  line-height: 1.5rem;
  font-weight: 500;
  color: #434657;
`;
const SelectionGroup = ({ title, options, onSelect, checked, type = 'checkbox', alternative = false, description, onChangeReason, otherReason, }) => {
    const isCheckReason = () => alternative && checked.includes(options[options.length - 1].id);
    const [reason, setReason] = useState(otherReason);
    const handleChange = (event) => {
        const value = event.target.value;
        setReason(value);
        onChangeReason(value);
    };
    const handleSelect = (id) => () => {
        onSelect(id);
    };
    return (React.createElement(Group, null,
        title && React.createElement(GroupTitle, null, title),
        description && (React.createElement(GroupDescription, { dangerouslySetInnerHTML: { __html: description } })),
        options.map(({ text, id }) => (React.createElement(Option, { key: id },
            React.createElement(StyledCheckbox, { onClick: handleSelect(id), checked: checked.includes(id), circle: type === 'radio', width: "1rem", height: "1rem" }),
            React.createElement(Text$1, { onClick: handleSelect(id), dangerouslySetInnerHTML: { __html: text } })))),
        isCheckReason() && (React.createElement(React.Fragment, null,
            React.createElement(AlternativeTitle, null, t('Nhập lý do')),
            React.createElement(Alternative, { spellCheck: false, placeholder: "Text...", rows: 3, onChange: handleChange, value: reason })))));
};

const Text = styled.a `
  &,
  &:hover,
  &:focus,
  &:visited {
    color: #047fff;
    text-decoration: none;
  }
`;
const TermAndPolicyText = ({ link = 'https://help.phongvu.vn', text = t('điều kiện sử dụng và chính sách'), className, }) => {
    return (React.createElement(Text, { href: link, target: "_blank", rel: "noopener noferrer", className: className, onClick: (event) => {
            event.stopPropagation();
        } }, text));
};

var PromoBlockStatus;
(function (PromoBlockStatus) {
    PromoBlockStatus["SELECTED"] = "selected";
    PromoBlockStatus["ACTIVE"] = "active";
    PromoBlockStatus["INACTIVE"] = "inactive";
})(PromoBlockStatus || (PromoBlockStatus = {}));
const Container = styled.div(({ hasBorderRadius, hasBorder, theme: { colors }, blockBackground, }) => `
    width: 100%;
    padding: 1rem 0 1rem 1rem;
    background-color: ${blockBackground ? blockBackground : 'white'};
    display: flex;
    align-items: center;
    border-bottom: ${hasBorder ? `1px solid ${colors.border}` : 'none'};
    border-radius: ${hasBorderRadius ? `8px` : '0'};
    margin-bottom: 12px;
  `);
const Left = styled.div(({ theme: { colors }, showRight }) => `
    border-right: ${showRight ? `1px dashed ${colors.border}` : `unset`};
    padding-right: 1rem;
    flex: 1;
  `);
const Description = styled.p(({ theme: { colors } }) => `
    margin: 0;
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1rem;
    color: ${colors.gray130};
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    margin-bottom: 0.75rem;
  `);
const Button = styled.span(({ isActive, theme: { colors } }) => `
    font-size: 0.8125rem;
    font-weight: 400;
    line-height: 1.25rem;
    color: ${isActive ? colors.link500 : colors.gray130};
    width: 6rem;
    text-align: center;
    padding: 0.5rem;

    &:hover {
      cursor: pointer;
    }
`);
const StyledPromoItem = styled(PromoItem) `
  &:not(:last-child) {
    margin-bottom: 0.75rem;
  }
`;
const PromoBlock = ({ benefits, description, status, hasBorder, hasBorderRadius = false, blockBackground, customBtn, onClick, showRight = true, }) => {
    const statusMap = {
        [PromoBlockStatus.SELECTED]: t('Bỏ chọn'),
        [PromoBlockStatus.ACTIVE]: t('Áp dụng'),
        [PromoBlockStatus.INACTIVE]: t('Mua thêm'),
    };
    return (React.createElement(Container, { hasBorder: hasBorder, hasBorderRadius: hasBorderRadius, blockBackground: blockBackground },
        React.createElement(Left, { showRight: showRight },
            description && React.createElement(Description, null, description),
            benefits.map(({ type, text, imgSrc }, index) => (React.createElement(StyledPromoItem, { type: type, text: text, imgSrc: imgSrc, key: index })))),
        showRight &&
            (customBtn || (React.createElement(Button, { isActive: status !== PromoBlockStatus.INACTIVE, onClick: onClick }, statusMap[status] || '')))));
};

export { Breadcrumb as B, DisplayStatus as D, FeaturedBanners as F, GoogleLogin as G, HorizontalSelect as H, Invoices as I, Logo as L, MiniSlides as M, OrderDetailInvoices as O, ProductCard as P, SearchSuggestions as S, TermAndPolicyText as T, UserCard as U, PolicyAndService as a, ProductReview as b, ProductsCarousel as c, ProductsGrid as d, InvoiceSectionKey as e, SideNav as f, ProductCoupon as g, ProductDiscount as h, Messenger as i, PreLoad as j, FormattedTime as k, FacebookLogin as l, PhoneLogin as m, SelectionGroup as n, PromoBlockStatus as o, PromoBlock as p };
